/**
 * AdminPersonaDetailPage - View/Edit AI Persona Details
 *
 * Feature: 071-ai-critique-sessions (US2, T045)
 * Displays AI persona details with edit functionality.
 */

import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  useAIPersona,
  useUpdateAIPersona,
  useExportAIPersona,
  getProviderDisplayName,
  CreateAIPersonaInput,
  UpdateAIPersonaInput,
} from '@/hooks/useAIPersonas';
import { PersonaForm } from '@/components/composed/PersonaForm';
import { IdLink } from '@/components/IdLink';
import logger from '@/utils/logger';
import { toast } from 'sonner';
import {
  adminAIPersonasUrl,
  adminAIPersonaDetailUrl,
  adminAIPersonaEditUrl,
} from '@/utils/urls';

/**
 * Download text content as a file
 */
function downloadFile(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function AdminPersonaDetailPage() {
  const { globalId } = useParams<{ globalId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = location.pathname.endsWith('/edit');

  const { data: persona, loading, error, refetch } = useAIPersona(globalId);
  const { updatePersona, loading: updateLoading } = useUpdateAIPersona();
  const { exportPersona } = useExportAIPersona();

  const [showEditForm, setShowEditForm] = useState(isEditMode);

  // Update edit mode based on URL
  useEffect(() => {
    setShowEditForm(isEditMode);
  }, [isEditMode]);

  const handleExport = async () => {
    if (!persona) return;
    try {
      const { markdown, filename } = await exportPersona(persona.globalId);
      downloadFile(markdown, filename);
      toast.success(`Exported ${persona.displayName}`);
    } catch (err) {
      logger.error('Admin', 'Failed to export persona', err);
      toast.error('Failed to export persona');
    }
  };

  const handleUpdate = async (data: CreateAIPersonaInput | UpdateAIPersonaInput) => {
    if (!persona) return;
    try {
      // For edit mode, data will be UpdateAIPersonaInput
      await updatePersona(persona.globalId, data as UpdateAIPersonaInput);
      toast.success('Persona updated successfully');
      setShowEditForm(false);
      navigate(adminAIPersonaDetailUrl(persona.globalId));
      refetch();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update persona';
      logger.error('Admin', 'Failed to update persona', err);
      toast.error(errorMessage);
    }
  };

  // Loading state
  if (loading && !persona) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-surface-2 rounded w-1/3"></div>
            <div className="h-64 bg-surface-2 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !persona) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-surface-1 rounded-lg border border-border p-8 text-center">
            <h2 className="text-xl font-semibold text-foreground mb-2">AI Persona Not Found</h2>
            <p className="text-muted mb-4">The requested AI persona could not be found.</p>
            <Link
              to={adminAIPersonasUrl()}
              className="inline-block px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Back to AI Personas
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {showEditForm ? 'Edit AI Persona' : persona.displayName}
            </h1>
            {!showEditForm && (
              <p className="text-muted mt-1">@{persona.username}</p>
            )}
          </div>
          <Link
            to={adminAIPersonasUrl()}
            className="px-4 py-2 border border-border rounded-md hover:bg-surface-2 transition-colors"
          >
            Back to List
          </Link>
        </div>

        {/* Edit Form */}
        {showEditForm ? (
          <div className="bg-surface-1 rounded-lg border border-border p-6">
            <PersonaForm
              persona={persona}
              onSubmit={handleUpdate}
              onCancel={() => {
                setShowEditForm(false);
                navigate(adminAIPersonaDetailUrl(persona.globalId));
              }}
              isSubmitting={updateLoading}
              submitLabel="Update Persona"
            />
          </div>
        ) : (
          /* View Mode */
          <div className="space-y-6">
            {/* Info Card */}
            <div className="bg-surface-1 rounded-lg border border-border p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-muted uppercase tracking-wider">
                      Username
                    </label>
                    <p className="text-foreground mt-1">@{persona.username}</p>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted uppercase tracking-wider">
                      Display Name
                    </label>
                    <p className="text-foreground mt-1">{persona.displayName}</p>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted uppercase tracking-wider">
                      ID
                    </label>
                    <div className="mt-1">
                      <IdLink globalId={persona.globalId} typeName="User" />
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-muted uppercase tracking-wider">
                      Provider
                    </label>
                    <p className="mt-1">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-500">
                        {getProviderDisplayName(persona.aiProvider)}
                      </span>
                    </p>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted uppercase tracking-wider">
                      Model
                    </label>
                    <p className="text-foreground mt-1 font-mono text-sm">{persona.aiModel}</p>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted uppercase tracking-wider">
                      Created
                    </label>
                    <p className="text-foreground mt-1">
                      {new Date(persona.createdAt).toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted uppercase tracking-wider">
                      Updated
                    </label>
                    <p className="text-foreground mt-1">
                      {new Date(persona.updatedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Prompt Card */}
            <div className="bg-surface-1 rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">System Prompt</h2>
                <span className="text-xs text-muted">
                  {persona.aiPrompt.length.toLocaleString()} characters
                </span>
              </div>
              <div className="bg-surface-2 rounded-md p-4 overflow-auto max-h-96">
                <pre className="text-sm text-foreground whitespace-pre-wrap font-mono">
                  {persona.aiPrompt}
                </pre>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={handleExport}
                className="px-4 py-2 border border-border rounded-md hover:bg-surface-2 transition-colors"
              >
                Export as Markdown
              </button>
              <button
                onClick={() => {
                  setShowEditForm(true);
                  navigate(adminAIPersonaEditUrl(persona.globalId));
                }}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                Edit Persona
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
